// ============================================================
// AI Initiative Review — types, scoring, and cross-site
// SharePoint persistence + rubric loading (single module).
//
// The rubric (pillars, questions, options, weights) is loaded
// at runtime from the PILLARS + QUESTIONS lists on the
// AISupportBot site — it is NOT hardcoded in the app. The only
// form-level constant here is META_FIELDS (the prefill fields),
// which the lists don't define.
//
// All Graph access targets the AISupportBot site, which is
// DIFFERENT from the app's main VITE_SP_SITE_ID — so the access
// below is self-contained. Authorized by the Sites.ReadWrite.All
// scope (see authConfig.ts).
// ============================================================

import { updateInitiativeScore, updateInitiativeStatus } from "./api/graph";
import { logActivity } from "./api/activityLog";

export type Perspective = "Business" | "Technical";

// ============================================================
// Config types
// ============================================================

/** One scored question (1–5 radio) with five option labels. */
export interface QuestionConfig {
  /** Stable key, e.g. "a1", "ba1", "r1". Used as the AnswersJSON key. */
  key: string;
  /** Short pillar-table label, e.g. "Infrastructure Fit". */
  label: string;
  /** Full prompt shown above the options. */
  prompt: string;
  /** "Consider:" guidance shown under the prompt. */
  consider?: string;
  /** Exactly five labels for scores 1 → 5, in order. */
  options: [string, string, string, string, string];
}

/** A weighted pillar grouping one or more questions. */
export interface PillarConfig {
  key: string;
  label: string;
  /** Decimal weight; weighted pillars must sum to 1.00 per perspective. */
  weight: number;
  questions: QuestionConfig[];
}

export interface PerspectiveConfig {
  perspective: Perspective;
  /** Weighted pillars (used in the composite score). */
  pillars: PillarConfig[];
  /** Unweighted standalone questions (e.g. R1 Stack Overlap). */
  extras: QuestionConfig[];
  /** Metadata text fields shown at the top of the form. */
  metaFields: { key: string; label: string; type: "text" | "date" | "textarea" }[];
}

export interface ReviewConfig {
  Business: PerspectiveConfig;
  Technical: PerspectiveConfig;
}

// ============================================================
// Persisted answer shape
// ============================================================

/** One answered question: a 1–5 score plus an optional note. */
export interface AnswerValue {
  score: number; // 1–5, or 0 / undefined when unanswered
  note?: string;
}

/** AnswersJSON shape stored on the REVIEWS list row. Keyed by question key. */
export type AnswersMap = Record<string, AnswerValue>;

/** A single risk flag derived from a score of 1 (critical) or 2 (concern). */
export interface RiskFlag {
  q: string;
  label: string;
  level: "critical" | "concern";
}

/** Normalized review row from the REVIEWS SharePoint list. */
export interface ReviewItem {
  id: string;
  initiativeId: number;
  perspective: Perspective;
  answers: AnswersMap;
  composite: number;
  verdict: string;
  riskFlags: RiskFlag[];
  r1Overlap?: number;
  status: "Current" | "Superseded";
  reviewerUpn: string;
  reviewerName: string;
  reviewDate: string; // ISO date (yyyy-mm-dd)
  modified?: string;
  raw: Record<string, unknown>;
}

// ============================================================
// Form-level constants
//
// META_FIELDS are the prefill/metadata inputs at the top of the
// form. They are UI scaffolding, not part of the scored rubric,
// and are intentionally NOT stored in the SharePoint lists.
// ============================================================

export const META_FIELDS: Record<Perspective, PerspectiveConfig["metaFields"]> = {
  Technical: [
    { key: "InitiativeName", label: "Initiative Name", type: "text" },
    { key: "ReviewDate", label: "Review Date", type: "date" },
    { key: "Description", label: "Description", type: "textarea" },
    { key: "ReviewedBy", label: "Reviewed By", type: "text" },
  ],
  Business: [
    { key: "InitiativeName", label: "Initiative Name", type: "text" },
    { key: "ReviewDate", label: "Review Date", type: "date" },
    { key: "BusinessSponsor", label: "Business Sponsor", type: "text" },
    { key: "BusinessDescription", label: "Business Description", type: "textarea" },
    { key: "ReviewedBy", label: "Reviewed By", type: "text" },
  ],
};

// ============================================================
// Scoring helpers — verbatim logic from the PoC form
// ============================================================

/** Mean of a pillar's answered questions — only valid when ALL are answered. */
export function pillarAvg(pillar: PillarConfig, answers: AnswersMap): number {
  let sum = 0;
  let n = 0;
  for (const q of pillar.questions) {
    const v = answers[q.key]?.score;
    if (v) {
      sum += v;
      n += 1;
    }
  }
  return n === pillar.questions.length && n > 0 ? sum / n : 0;
}

/** Weighted composite — only valid when every pillar is fully answered. */
export function compositeScore(cfg: PerspectiveConfig, answers: AnswersMap): number {
  let total = 0;
  let valid = true;
  for (const p of cfg.pillars) {
    const avg = pillarAvg(p, answers);
    if (!avg) valid = false;
    total += avg * p.weight;
  }
  return valid ? total : 0;
}

/** Bar / value color band by 1–5 value. */
export function barColor(val: number): string {
  if (val >= 4) return "var(--green)";
  if (val >= 3) return "var(--gold)";
  if (val >= 2) return "var(--orange)";
  return "var(--red)";
}

export interface VerdictInfo {
  text: string;
  color: string;
  bg: string;
}

/** Technical verdict bands. */
export function technicalVerdict(score: number): VerdictInfo {
  if (score >= 4.0) return { text: "Strong Candidate", color: "var(--green)", bg: "var(--green-bg)" };
  if (score >= 3.0) return { text: "Viable with Conditions", color: "var(--yellow)", bg: "var(--yellow-bg)" };
  if (score >= 2.0) return { text: "Needs Significant Work", color: "var(--orange)", bg: "#FFF4ED" };
  return { text: "High Risk", color: "var(--red)", bg: "var(--red-bg)" };
}

/** Business verdict bands. */
export function businessVerdict(score: number): VerdictInfo {
  if (score >= 4.0) return { text: "High Priority", color: "var(--green)", bg: "var(--green-bg)" };
  if (score >= 3.0) return { text: "Worth Pursuing", color: "var(--yellow)", bg: "var(--yellow-bg)" };
  if (score >= 2.0) return { text: "Marginal Value", color: "var(--orange)", bg: "#FFF4ED" };
  return { text: "Low Priority", color: "var(--red)", bg: "var(--red-bg)" };
}

export function verdictFor(perspective: Perspective, score: number): VerdictInfo {
  return perspective === "Business" ? businessVerdict(score) : technicalVerdict(score);
}

/** Derive risk flags: score of 1 → critical, 2 → concern. Includes extras. */
export function deriveRiskFlags(cfg: PerspectiveConfig, answers: AnswersMap): RiskFlag[] {
  const flags: RiskFlag[] = [];
  const allQs = [...cfg.pillars.flatMap((p) => p.questions), ...cfg.extras];
  for (const q of allQs) {
    const v = answers[q.key]?.score;
    if (v === 1) flags.push({ q: q.key.toUpperCase(), label: q.label, level: "critical" });
    else if (v === 2) flags.push({ q: q.key.toUpperCase(), label: q.label, level: "concern" });
  }
  return flags;
}

/** True when every weighted question has a 1–5 score (extras excluded). */
export function isComplete(cfg: PerspectiveConfig, answers: AnswersMap): boolean {
  return cfg.pillars.every((p) =>
    p.questions.every((q) => {
      const v = answers[q.key]?.score;
      return typeof v === "number" && v >= 1 && v <= 5;
    }),
  );
}

// ============================================================
// SharePoint persistence — cross-site Graph access
// ============================================================

const GRAPH_BASE = "https://graph.microsoft.com/v1.0";
const PREFER = "HonorNonIndexedQueriesWarningMayFailRandomly";

// ---------- Site + list IDs ----------
// Overridable via env; falls back to the known AISupportBot values.

const REVIEW_SITE_ID =
  import.meta.env.VITE_SP_REVIEW_SITE_ID ??
  "brookfieldrp.sharepoint.com,67235556-d419-47bf-95c5-dd91303f43a5,0bd26820-cd01-4198-ac0b-c9e6233db1b1";

export const REVIEWS_LIST_ID =
  import.meta.env.VITE_SP_REVIEWS_LIST_ID ?? "df8041b7-4410-4133-9087-d8a86fbff14a";

export const PILLARS_LIST_ID =
  import.meta.env.VITE_SP_PILLARS_LIST_ID ?? "e492cddc-011d-4029-b42d-e2a9c9e85f0a";

export const QUESTIONS_LIST_ID =
  import.meta.env.VITE_SP_QUESTIONS_LIST_ID ?? "aeca0935-649c-4c57-937b-af0594d99663";

// ---------- Raw shapes ----------

interface RawItem {
  id: string;
  lastModifiedDateTime?: string;
  fields: Record<string, unknown>;
}
interface RawResponse {
  value: RawItem[];
  "@odata.nextLink"?: string;
}

function str(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
}
function num(v: unknown): number | undefined {
  if (v == null || v === "") return undefined;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
}
function parseJson<T>(v: unknown, fallback: T): T {
  const s = str(v);
  if (!s) return fallback;
  try {
    return JSON.parse(s) as T;
  } catch {
    return fallback;
  }
}

async function graphFetch(token: string, url: string, init?: RequestInit): Promise<Response> {
  return fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      Prefer: PREFER,
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });
}

async function ensureOk(res: Response, verb: string): Promise<void> {
  if (res.ok || res.status === 204) return;
  const text = await res.text().catch(() => "");
  throw new Error(
    `Graph ${verb} ${res.status}: ${res.statusText}${text ? ` — ${text.slice(0, 300)}` : ""}`,
  );
}

// ---------- Mapper ----------

function mapReview(raw: RawItem): ReviewItem {
  const f = raw.fields;
  return {
    id: raw.id,
    initiativeId: num(f.InitiativeId) ?? 0,
    perspective: (str(f.Perspective) as Perspective) || "Business",
    answers: parseJson<AnswersMap>(f.AnswersJSON, {}),
    composite: num(f.Composite) ?? 0,
    verdict: str(f.Verdict),
    riskFlags: parseJson<RiskFlag[]>(f.RiskFlagsJSON, []),
    r1Overlap: num(f.R1Overlap),
    status: (str(f.Status) as ReviewItem["status"]) || "Current",
    reviewerUpn: str(f.ReviewerUpn),
    reviewerName: str(f.ReviewerName),
    reviewDate: str(f.ReviewDate).slice(0, 10),
    modified: raw.lastModifiedDateTime,
    raw: f,
  };
}

// ---------- Reads ----------

/**
 * Load the Current review for a given initiative + perspective, if one
 * exists. Used to preload the form so admins can update an existing score.
 */
export async function getCurrentReview(
  token: string,
  initiativeId: number,
  perspective: Perspective,
): Promise<ReviewItem | null> {
  const filter = encodeURIComponent(
    `fields/InitiativeId eq ${initiativeId} and fields/Perspective eq '${perspective}' and fields/Status eq 'Current'`,
  );
  const url =
    `${GRAPH_BASE}/sites/${REVIEW_SITE_ID}/lists/${REVIEWS_LIST_ID}/items` +
    `?expand=fields&$filter=${filter}&$top=1`;
  const res = await graphFetch(token, url);
  await ensureOk(res, "get");
  const data = (await res.json()) as RawResponse;
  return data.value[0] ? mapReview(data.value[0]) : null;
}

/** Load both Current reviews (Business + Technical) for an initiative. */
export async function getReviewsForInitiative(
  token: string,
  initiativeId: number,
): Promise<{ business: ReviewItem | null; technical: ReviewItem | null }> {
  const filter = encodeURIComponent(
    `fields/InitiativeId eq ${initiativeId} and fields/Status eq 'Current'`,
  );
  const url =
    `${GRAPH_BASE}/sites/${REVIEW_SITE_ID}/lists/${REVIEWS_LIST_ID}/items` +
    `?expand=fields&$filter=${filter}&$top=10`;
  const res = await graphFetch(token, url);
  await ensureOk(res, "get");
  const data = (await res.json()) as RawResponse;
  const rows = data.value.map(mapReview);
  return {
    business: rows.find((r) => r.perspective === "Business") ?? null,
    technical: rows.find((r) => r.perspective === "Technical") ?? null,
  };
}

// ---------- Write (upsert) ----------

export interface SaveReviewInput {
  initiativeId: number;
  perspective: Perspective;
  answers: AnswersMap;
  reviewerUpn: string;
  reviewerName: string;
  reviewDate: string; // yyyy-mm-dd
  /** R1 Stack Overlap is captured separately from the weighted score. */
  r1Overlap?: number;
}

function buildFields(input: SaveReviewInput, cfg: ReviewConfig): Record<string, unknown> {
  const persp = cfg[input.perspective];
  const composite = compositeScore(persp, input.answers);
  const flags = deriveRiskFlags(persp, input.answers);
  const verdict = composite ? verdictFor(input.perspective, composite).text : "Incomplete";
  return {
    InitiativeId: input.initiativeId,
    Perspective: input.perspective,
    AnswersJSON: JSON.stringify(input.answers),
    Composite: Number(composite.toFixed(2)),
    Verdict: verdict,
    RiskFlagsJSON: JSON.stringify(flags),
    ...(input.r1Overlap != null ? { R1Overlap: input.r1Overlap } : {}),
    Status: "Current",
    ReviewerUpn: input.reviewerUpn,
    ReviewerName: input.reviewerName,
    ReviewDate: input.reviewDate,
  };
}

/**
 * Upsert the Current review row, keyed by InitiativeId + Perspective.
 * If a Current row exists it is PATCHed in place (admins updating the
 * score via preload); otherwise a new row is POSTed. This keeps exactly
 * one Current row per (initiative, perspective).
 *
 * `cfg` is the runtime rubric loaded from SharePoint via loadReviewConfig
 * — required so the stored Composite/Verdict/RiskFlags match the live
 * pillar weights, not a stale hardcoded copy.
 */
export async function saveReview(
  token: string,
  input: SaveReviewInput,
  cfg: ReviewConfig,
): Promise<ReviewItem> {
  const fields = buildFields(input, cfg);
  const existing = await getCurrentReview(token, input.initiativeId, input.perspective);

  let saved: ReviewItem;
  if (existing) {
    const url = `${GRAPH_BASE}/sites/${REVIEW_SITE_ID}/lists/${REVIEWS_LIST_ID}/items/${existing.id}/fields`;
    const res = await graphFetch(token, url, { method: "PATCH", body: JSON.stringify(fields) });
    await ensureOk(res, "update");
    saved = { ...existing, ...mapReview({ id: existing.id, fields }) };
  } else {
    const url = `${GRAPH_BASE}/sites/${REVIEW_SITE_ID}/lists/${REVIEWS_LIST_ID}/items`;
    const res = await graphFetch(token, url, { method: "POST", body: JSON.stringify({ fields }) });
    await ensureOk(res, "create");
    const created = (await res.json()) as RawItem;
    saved = mapReview({ id: created.id, fields: created.fields ?? fields });
  }

  // Denormalize the composite back onto the Initiative list row: a Business
  // review writes BusinessValueScore, a Technical review writes
  // TechnicalFeasibilityScore. Best-effort — the review is the source of
  // truth, so a failed write-back must not fail the save. Skipped when the
  // composite is 0 (incomplete) to avoid clobbering an existing score.
  if (saved.composite > 0) {
    const field =
      input.perspective === "Business" ? "BusinessValueScore" : "TechnicalFeasibilityScore";
    try {
      await updateInitiativeScore(token, String(input.initiativeId), field, saved.composite);
    } catch (e) {
      console.warn(`Review saved, but writing ${field} to the initiative failed:`, e);
    }

    // Advance the initiative's workflow Status as each review stage finishes:
    //   Business review done  → "Under Technical Review"
    //   Technical review done → "In Backlog"
    // Best-effort, like the score write-back above — a failed Status PATCH
    // must not fail the save. Re-saving a Business review after a Technical
    // score already exists must NOT drag the status back to "Under Technical
    // Review", so that one move is skipped when a Technical review is on file.
    try {
      let nextStatus: string | null = null;
      if (input.perspective === "Technical") {
        nextStatus = "In Backlog";
      } else {
        const tech = await getCurrentReview(token, input.initiativeId, "Technical");
        if (!tech || tech.composite <= 0) nextStatus = "Under Technical Review";
      }
      if (nextStatus) {
        await updateInitiativeStatus(token, String(input.initiativeId), nextStatus);
      }
    } catch (e) {
      console.warn("Review saved, but advancing the initiative status failed:", e);
    }
  }

  void logActivity(
    "Review",
    "Saved",
    `${input.reviewerName || "Someone"} saved a ${input.perspective} review for initiative ${input.initiativeId} (score ${saved.composite.toFixed(2)})`,
    "review",
    String(input.initiativeId)
  );

  return saved;
}

// ============================================================
// Rubric loading — the single source of truth
//
// The pillars, questions, option labels, and weights all come
// from the PILLARS + QUESTIONS lists. There is no hardcoded
// rubric fallback: if the lists are empty, loadReviewConfig
// throws so the UI can surface a clear "not configured" state
// instead of silently showing stale content.
// ============================================================

interface RawPillar {
  perspective: Perspective;
  key: string;
  label: string;
  weight: number;
}
interface RawQuestion {
  perspective: Perspective;
  pillar: string;
  order: number;
  key: string;
  label: string;
  description: string;
  options: string[];
}

/**
 * Normalize an OptionLabelsJSON array to exactly five score labels.
 * The lists store six entries with a leading blank for the unscored
 * (0) state — e.g. ["", "New platform", …, "Native fit"] — so we drop
 * the leading blank. Returns null if the shape can't yield five labels.
 */
function normalizeOptions(raw: string[]): QuestionConfig["options"] | null {
  let o = raw;
  if (o.length === 6 && o[0] === "") o = o.slice(1);
  return o.length === 5 ? (o as QuestionConfig["options"]) : null;
}

/** Split a "<prompt> Consider: <guidance>" Description into its two parts. */
function splitConsider(desc: string): { prompt: string; consider?: string } {
  const marker = "Consider:";
  const i = desc.indexOf(marker);
  if (i === -1) return { prompt: desc.trim() };
  return {
    prompt: desc.slice(0, i).trim(),
    consider: desc.slice(i + marker.length).trim() || undefined,
  };
}

function toQuestion(q: RawQuestion): QuestionConfig | null {
  const options = normalizeOptions(q.options);
  if (!options) return null;
  const { prompt, consider } = splitConsider(q.description);
  return { key: q.key, label: q.label, prompt, consider, options };
}

function buildPerspective(
  perspective: Perspective,
  pillarRows: RawPillar[],
  questionRows: RawQuestion[],
): PerspectiveConfig {
  const myPillars = pillarRows.filter((p) => p.perspective === perspective);
  const myQuestions = questionRows
    .filter((q) => q.perspective === perspective)
    .sort((a, b) => a.order - b.order);

  const pillars = myPillars
    .filter((p) => p.weight > 0)
    .map((p) => ({
      key: p.key,
      label: p.label,
      weight: p.weight,
      questions: myQuestions
        .filter((q) => q.pillar === p.key)
        .map(toQuestion)
        .filter((q): q is QuestionConfig => q !== null),
    }))
    .filter((p) => p.questions.length > 0);

  // Questions whose pillar is unweighted (weight 0) or missing become
  // standalone, unscored "extras" — e.g. R1 Stack Overlap.
  const extras = myQuestions
    .filter((q) => {
      const owner = myPillars.find((p) => p.key === q.pillar);
      return !owner || owner.weight === 0;
    })
    .map(toQuestion)
    .filter((q): q is QuestionConfig => q !== null);

  return { perspective, pillars, extras, metaFields: META_FIELDS[perspective] };
}

/**
 * Build the full ReviewConfig from the PILLARS + QUESTIONS lists.
 * This is the only rubric source — call it once on mount and pass the
 * result into the modal. Throws if either list is empty.
 */
export async function loadReviewConfig(token: string): Promise<ReviewConfig> {
  const [pillarsRes, questionsRes] = await Promise.all([
    graphFetch(
      token,
      `${GRAPH_BASE}/sites/${REVIEW_SITE_ID}/lists/${PILLARS_LIST_ID}/items?expand=fields&$top=100`,
    ),
    graphFetch(
      token,
      `${GRAPH_BASE}/sites/${REVIEW_SITE_ID}/lists/${QUESTIONS_LIST_ID}/items?expand=fields&$top=200`,
    ),
  ]);
  await ensureOk(pillarsRes, "get");
  await ensureOk(questionsRes, "get");

  const pillarRows = ((await pillarsRes.json()) as RawResponse).value
    .filter((r) => str(r.fields.Active) !== "false" && r.fields.Active !== false)
    .map<RawPillar>((r) => ({
      perspective: (str(r.fields.Perspective) as Perspective) || "Business",
      key: str(r.fields.Title),
      label: str(r.fields.Label),
      weight: num(r.fields.Weight) ?? 0,
    }));

  const questionRows = ((await questionsRes.json()) as RawResponse).value
    .filter((r) => str(r.fields.Active) !== "false" && r.fields.Active !== false)
    .map<RawQuestion>((r) => ({
      perspective: (str(r.fields.Perspective) as Perspective) || "Business",
      pillar: str(r.fields.Pillar),
      order: num(r.fields.Order0) ?? 0,
      key: str(r.fields.Title),
      label: str(r.fields.Label),
      description: str(r.fields.Description),
      options: parseJson<string[]>(r.fields.OptionLabelsJSON, []),
    }));

  if (pillarRows.length === 0 || questionRows.length === 0) {
    throw new Error(
      "Review rubric is not configured. Add rows to the AI Review Pillars and AI Review Questions lists.",
    );
  }

  return {
    Business: buildPerspective("Business", pillarRows, questionRows),
    Technical: buildPerspective("Technical", pillarRows, questionRows),
  };
}
